import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useInfo } from "@/context/InfoContext";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminServices from "@/pages/admin/AdminServices";
import AdminBlocks from "@/pages/admin/AdminBlocks";
import AdminClients from "@/pages/admin/AdminClients";
import AdminGallery from "@/pages/admin/AdminGallery";
import AdminSettings from "@/pages/admin/AdminSettings";

const TABS = [
  ["bookings", "Reservas", AdminBookings],
  ["services", "Servicios", AdminServices],
  ["blocks", "Bloqueos", AdminBlocks],
  ["clients", "Clientes", AdminClients],
  ["gallery", "Galería", AdminGallery],
  ["settings", "Ajustes", AdminSettings],
];

const NoAccess = ({ email, onRetry, error }) => (
  <section className="max-w-xl mx-auto px-4 sm:px-6 py-20 sm:py-28" data-testid="admin-no-access">
    <ShieldAlert className="w-8 h-8 mb-6" />
    <h1 className="font-serif text-3xl sm:text-4xl tracking-tight">{error ? "No se pudo comprobar tu acceso" : "Acceso solo para administradores"}</h1>
    {error ? (
      <p className="mt-4 text-zinc-600">Ha fallado la comprobación de permisos. Inténtalo de nuevo en unos segundos.</p>
    ) : (
      <>
        <p className="mt-4 text-zinc-600">Has iniciado sesión como <span className="font-semibold text-[#111111]" data-testid="admin-no-access-email">{email || "—"}</span>, que no tiene permisos de administrador.</p>
        <p className="mt-4 text-sm text-zinc-500">Si eres el propietario, cierra sesión y entra con el correo de propietario configurado (y verificado). Si necesitas acceso, pídeselo al propietario del salón: puede darte el rol de administrador desde Panel admin → Clientes.</p>
      </>
    )}
    <div className="mt-8 flex flex-wrap gap-3">
      <button onClick={onRetry} className="h-12 px-6 bg-[#111111] text-white text-xs tracking-[0.2em] uppercase" data-testid="admin-no-access-retry">Volver a comprobar</button>
      <Link to="/" className="h-12 px-6 inline-flex items-center border border-zinc-300 text-xs tracking-[0.2em] uppercase" data-testid="admin-no-access-home">Ir al inicio</Link>
    </div>
  </section>
);

export default function Admin() {
  const { me, meStatus, reloadMe } = useInfo();
  const [tab, setTab] = useState("bookings");
  if (meStatus === "error") return <NoAccess onRetry={reloadMe} error />;
  if (!me) return <div className="py-32 flex justify-center text-zinc-400" data-testid="admin-loading"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (me.role !== "admin") return <NoAccess email={me.email} onRetry={reloadMe} />;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="admin-page">
      <p className="text-xs tracking-[0.3em] uppercase text-zinc-500">Panel privado</p>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3 mb-8">Administración</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full justify-start overflow-x-auto h-auto bg-transparent border-b border-zinc-200 rounded-none p-0 gap-1">
          {TABS.map(([k, l]) => (
            <TabsTrigger key={k} value={k} data-testid={`admin-tab-${k}`} className="rounded-none px-4 py-3 text-xs tracking-[0.15em] uppercase data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#111111]">
              {l}
            </TabsTrigger>
          ))}
        </TabsList>
        {TABS.map(([k, , C]) => (
          <TabsContent key={k} value={k} className="pt-8">{tab === k && <C />}</TabsContent>
        ))}
      </Tabs>
    </section>
  );
}
